import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Spinner,
  IconButton,
  useToast,
  useBreakpointValue,
  Flex,
  Divider,
  Image,
  SimpleGrid,
} from '@chakra-ui/react'
import { FiArrowLeft, FiEye } from 'react-icons/fi'
import { getNoticeDetail } from '../services/apiService'
import dayjs from 'dayjs'

interface NoticeDetail {
  id: number
  userId: number
  username: string
  title: string
  content: string
  images?: string[] | null
  viewCount: number
  createdAt: string
  updatedAt: string | null
}

const NoticeDetail: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, md: false })

  const [notice, setNotice] = useState<NoticeDetail | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchNotice = async () => {
    if (!id) return
    setLoading(true)
    try {
      const { data: res } = await getNoticeDetail(parseInt(id))
      if (res.statusCode === 200) {
        setNotice(res.data)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('공지사항을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      navigate('/notice')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotice()
  }, [id])

  if (loading) {
    return (
      <DefaultLayout>
        <Container maxW="container.xl" py={10}>
          <Box textAlign="center" py={10}>
            <Spinner size="xl" color="brand.500" />
          </Box>
        </Container>
      </DefaultLayout>
    )
  }

  if (!notice) {
    return null
  }

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          {/* 헤더 */}
          <Flex
            direction={isMobile ? 'column' : 'row'}
            justify="space-between"
            align={isMobile ? 'flex-start' : 'center'}
            gap={4}
          >
            <HStack spacing={4}>
              <IconButton
                aria-label={t('뒤로가기')}
                icon={<FiArrowLeft />}
                onClick={() => navigate('/notice')}
                variant="ghost"
              />
              <Heading
                as="h1"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
              >
                {t('공지사항')}
              </Heading>
            </HStack>
          </Flex>

          {/* 공지사항 내용 */}
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={6}>
                {/* 제목 */}
                <Box>
                  <Heading size="lg" mb={4}>
                    {notice.title}
                  </Heading>
                  <Divider />
                </Box>

                {/* 메타 정보 */}
                <HStack justify="space-between" fontSize="sm" color="gray.500" flexWrap="wrap">
                  <HStack spacing={4}>
                    <Text>{notice.username}</Text>
                    <Text>•</Text>
                    <Text>{dayjs(notice.createdAt).format('YYYY.MM.DD HH:mm')}</Text>
                    {notice.updatedAt && (
                      <>
                        <Text>•</Text>
                        <Text>{t('수정됨')}: {dayjs(notice.updatedAt).format('YYYY.MM.DD HH:mm')}</Text>
                      </>
                    )}
                  </HStack>
                  <HStack spacing={2}>
                    <FiEye />
                    <Text>{notice.viewCount}</Text>
                  </HStack>
                </HStack>

                <Divider />

                {/* 이미지 */}
                {notice.images && notice.images.length > 0 && (
                  <Box>
                    <SimpleGrid columns={{ base: 1, md: notice.images.length > 1 ? 2 : 1 }} spacing={4}>
                      {notice.images.map((imageUrl, index) => (
                        <Image
                          key={index}
                          src={imageUrl}
                          alt={`공지사항 이미지 ${index + 1}`}
                          borderRadius="md"
                          objectFit="cover"
                          w="100%"
                          maxH="500px"
                        />
                      ))}
                    </SimpleGrid>
                    <Divider mt={6} />
                  </Box>
                )}

                {/* 내용 */}
                <Box>
                  <Text
                    whiteSpace="pre-wrap"
                    fontSize="md"
                    lineHeight="1.8"
                    color="gray.700"
                  >
                    {notice.content}
                  </Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* 목록으로 버튼 */}
          <Box textAlign="center">
            <Button
              onClick={() => navigate('/notice')}
              colorScheme="brand"
            >
              {t('목록으로')}
            </Button>
          </Box>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default NoticeDetail

